/**
 * Utility for working with translated messages from Twig
 */
class Messages {
  constructor(formElement) {
    this.messages = {};
    this.form = formElement;
    this.init();
  }

  /**
   * Initialize - read messages from data attribute
   */
  init() {
    const messagesData = this.form?.dataset.messages;
    if (messagesData) {
      try {
        this.messages = JSON.parse(messagesData);
      } catch (e) {
        console.error('Error parsing messages from data attribute:', e);
        this.messages = {};
      }
    }
  }

  /**
   * Get message by key
   * @param {string} key - Message key
   * @param {object} params - Parameters for substitution (e.g., {min: 6})
   * @returns {string} Translated message
   */
  getMessage(key, params = {}) {
    let message = this.messages[key] || key;

    // Substitute parameters in message
    if (Object.keys(params).length > 0) {
      message = Messages.formatMessage(message, params);
    }

    return message;
  }

  /**
   * Format message with parameters
   * Supports %d, %s format and named parameters {param}
   * @param {string} message - Message template
   * @param {object} params - Parameters for substitution
   * @returns {string} Formatted message
   */
  static formatMessage(message, params) {
    let formatted = message;

    // Support for %d, %s (order matters)
    if (typeof params === 'object') {
      // For %d and %s use parameter order
      const paramValues = Object.values(params);
      formatted = formatted.replace(/%[ds]/g, (match) => {
        const value = paramValues.shift();
        return value !== undefined ? String(value) : match;
      });

      // Support for named parameters {param}
      Object.keys(params).forEach((key) => {
        const regex = new RegExp(`\\{${key}\\}`, 'g');
        formatted = formatted.replace(regex, params[key]);
      });
    }

    return formatted;
  }

  /**
   * Check if message exists
   * @param {string} key - Message key
   * @returns {boolean}
   */
  hasMessage(key) {
    return key in this.messages;
  }

  /**
   * Get message from API response
   * If message is a key, returns translated message
   * If message is plain text, returns it as is
   * @param {string} message - Message or key from API
   * @param {object} params - Parameters for substitution
   * @returns {string}
   */
  getMessageFromApi(message, params = {}) {
    if (!message) {
      return '';
    }

    // If message is a key (starts with error_ or message_)
    if (this.hasMessage(message)) {
      return this.getMessage(message, params);
    }

    // If it's plain text, return as is
    return message;
  }
}

export default Messages;
